import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { motion } from "framer-motion";
import { Loader2, CheckCircle, XCircle, Sparkles } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "polling", pkg: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", pkg: null });
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          await refresh();
          setState({ status: "paid", pkg: data.package_id });
          return;
        }
        if (data.session_status === "expired") {
          setState({ status: "expired", pkg: data.package_id });
          return;
        }
        if (attemptsRef.current >= 8) {
          setState({ status: "timeout", pkg: data.package_id });
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        if (attemptsRef.current >= 8) {
          setState({ status: "error", pkg: null });
        } else {
          setTimeout(tick, 2500);
        }
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen grain-bg flex items-center justify-center px-6" data-testid="billing-success">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-[#E5DFD3] rounded-3xl p-10 max-w-md w-full text-center shadow-[0_30px_60px_-25px_rgba(0,0,0,0.15)]">
        {state.status === "polling" && (
          <>
            <Loader2 size={32} className="mx-auto animate-spin text-[#913831]" />
            <h2 className="font-serif text-3xl mt-5">Confirming your payment…</h2>
            <p className="text-[#737373] text-sm mt-2">A graceful pause. Stripe is being very Swiss about this.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <motion.div initial={{ scale: 0.5 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 120 }}>
              <Sparkles size={36} className="mx-auto text-[#913831]" strokeWidth={1.5} />
            </motion.div>
            <h2 className="font-serif text-4xl mt-5" data-testid="payment-paid">Welcome to Quiet+.</h2>
            <p className="text-[#737373] mt-3">
              {state.pkg === "quietplus_monthly"
                ? "Unlimited likes and a discreet admirers list, on the house for the next 30 days."
                : "Five thoughtful notes added to your account. Use them with care."}
            </p>
            <button data-testid="back-to-app" onClick={() => navigate("/app")}
              className="mt-7 bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full px-7 py-3.5 font-medium">
              Back to Rendezvous
            </button>
          </>
        )}
        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <>
            <XCircle size={32} className="mx-auto text-[#D9534F]" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl mt-5">Payment not completed.</h2>
            <p className="text-[#737373] mt-3 text-sm">
              {state.status === "expired" ? "Your session expired before checkout finished." :
               state.status === "timeout" ? "It's taking longer than usual. Check your billing page in a moment." :
               "We couldn't confirm the payment. No charge if you didn't complete checkout."}
            </p>
            <div className="mt-7 flex gap-3 justify-center">
              <button data-testid="retry-billing" onClick={() => navigate("/billing")}
                className="border border-[#E5DFD3] hover:bg-[#F0EBE1] rounded-full px-5 py-2.5 text-sm">
                Try again
              </button>
              <button data-testid="back-to-app-error" onClick={() => navigate("/app")}
                className="bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full px-5 py-2.5 text-sm">
                Back to app
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
